import React, { useState, useRef, useEffect } from "react"
import Card from "@mui/material/Card"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"

import { useNavigate } from "react-router-dom"

export interface FacilityLogItem {
  facility_id: string
  medical_record_id: string
  facility_name: string
  facility_address: string
  medical_record_room_no: number
  log_create_date: number
  detection: number
  device_state: number
}

// ロケール対応の日付フォーマット関数
const formatDateWithLocale = (timestamp: number, locale: string = "ja-JP"): string => {
  const date = new Date(timestamp * 1000)
  //console.log(timestamp);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false // 24時間形式
  }).format(date)
}

interface DetectionListProps {
  detections: FacilityLogItem[]
}

function DetectionList({ detections }: DetectionListProps): React.ReactElement {
  const [visibleItems, setVisibleItems] = useState(detections.slice(0, 5))
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [showMoveMedicalRecord, setShowMoveMedicalRecord] = useState<boolean>(false)

  const navigate = useNavigate()

  const handleMenuSelect = (facility: string, medical_record: string) => {
    //console.log(facility, medical_record);
    navigate("/tenant/" + facility + "/" + medical_record)
  }

  // スクロール時の処理
  const handleScroll = () => {
    const contentEl = contentRef.current
    if (!contentEl) return

    // スクロールが末尾に近づいたら追加読み込み
    if (contentEl.scrollTop + contentEl.clientHeight >= contentEl.scrollHeight - 10) {
      setVisibleItems((prevVisibleItems) => {
        const nextIndex = prevVisibleItems.length
        const nextItems = detections.slice(nextIndex, nextIndex + 5) // さらに5件取得
        return [...prevVisibleItems, ...nextItems]
      })
    }
  }

  // スクロールイベントを登録
  useEffect(() => {
    setVisibleItems((prevVisibleItems) => {
      const nextIndex = prevVisibleItems.length
      const nextItems = detections.slice(nextIndex, nextIndex + 5) // さらに5件取得
      return [...prevVisibleItems, ...nextItems]
    })

    const contentEl = contentRef.current
    if (contentEl) {
      contentEl.addEventListener("scroll", handleScroll)
    }
    return () => {
      if (contentEl) {
        contentEl.removeEventListener("scroll", handleScroll)
      }
    }
  }, [detections])

  const renderIcon = (icon: "warning" | "alert"): React.ReactElement => {
    const iconProps = {
      warning: { color: "warning", icon: "circle_notifications" },
      alert: { color: "primary", icon: "report_problem" }
    }

    return (
      <Icon component="span" color={iconProps[icon].color as "warning" | "primary"} fontSize="medium">
        {iconProps[icon].icon}
      </Icon>
    )
  }

  // アカウントに応じたボタン表示処理
  useEffect(() => {
    // 開発者アカウントでも、入居者詳細画面に遷移可能とするため、コメントアウト
    setShowMoveMedicalRecord(true)
    /*
    const account_type = localStorage.getItem("account_type")
    if (account_type != "service") {
      setShowMoveMedicalRecord(true)
    }
    */
  }, [])

  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            不在検知/機器エラー一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox>
        <TableContainer ref={contentRef} sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "330px" }}>
          <Table>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold"
                  }
                }}
              >
                <TableCell align="center">状態</TableCell>
                <TableCell align="left">施設ID</TableCell>
                <TableCell align="left">施設名</TableCell>
                <TableCell align="left">入居者(居室)番号</TableCell>
                <TableCell align="right">不在検知/機器エラー日時</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal"
                    }
                  }}
                >
                  <TableCell align="center">
                    {row.device_state === 1 && row.detection === 0 ? renderIcon("warning") : renderIcon("alert")}
                  </TableCell>
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={row.facility_id || undefined}
                    >
                      {row.facility_id}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.facility_name}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "50px" }}>{row.medical_record_room_no}</div>
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ minWidth: "100px" }}>{formatDateWithLocale(row.log_create_date)}</div>
                  </TableCell>
                  {showMoveMedicalRecord && (
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ color: "#FFF" }}
                        onClick={() => {
                          handleMenuSelect(`${row.facility_id}`, `${row.medical_record_id}`)
                        }}
                      >
                        確認
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    </Card>
  )
}

export default DetectionList
